/* eslint-disable consistent-return */
import { findLast, isNil, keys, maxBy, minBy, round } from 'lodash'
import { PASTEL_COLOR } from './constants/ColorTheme'
import i18n from 'simple-react-i18n'
import { calculateValue } from './AnalyseUtils'

/*
 *               ⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⢀⣤⡶⠿⠿⢶⣄⡀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀
 *               ⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⢠⡿⠃⠀⠀⠀⠀⠙⢷⡄⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀
 *               ⠀⠀⠀⠀⠀⠀⠀⠀⠀⣠⡿⢡⠀⠀⠀⠀⠀⢀⡈⢿⡄⠀⠀⠀⠀⠀⠀⠀⠀⠀
 *               ⠀⠀⠀⠀⠀⠀⠀⠀⣰⡟⢠⣿⠀⠀⠀⠀⠀⢸⣷⡈⢻⣆⠀⠀⠀⠀⠀⠀⠀⠀
 *               ⠀⠀⠀⠀⠀⠀⢀⣼⠏⢠⣿⣿⡆⠀⠀⠀⠀⣸⣿⣷⡄⠹⣆⠀⠀⠀⠀⠀⠀⠀
 *               ⠀⠀⠀⠀⠀⢀⣾⠃⣰⣿⣿⣿⡇⠀⠀⠀⠀⣿⣿⣿⣿⡄⠹⣷⡀⠀⠀⠀⠀⠀
 *               ⠀⠀⠀⠀⢠⡿⠁⣰⣿⣿⣿⣿⣿⠀⠀⠀⢠⣿⣿⣿⣿⣿⣆⠘⣷⡀⠀⠀⠀⠀
 *               ⠀⠀⠀⢠⡿⠁⣼⣿⣿⣿⣿⣿⣿⠀⠀⠀⢸⣿⣿⣿⣿⣿⣿⣆⠘⢿⡄⠀⠀⠀
 *               ⠀⠀⣠⡟⢀⣼⣿⣿⣿⣿⣿⣿⣿⣇⠀⠀⣼⣿⣿⣿⣿⣿⣿⣿⣧⠈⢿⡄⠀⠀
 *               ⠀⢰⡿⠀⣾⣿⣿⣿⣿⣿⣿⣿⣿⡟⠛⠛⣿⣿⣿⣿⣿⣿⣿⣿⣿⣧⠈⣿⡄⠀
 *               ⠀⢼⡇⢸⣿⣿⣿⣿⣿⣿⣿⣿⣿⡇⠀⠀⣿⣿⣿⣿⣿⣿⣿⣿⣿⣿⠆⢸⡇⠀
 *               ⠀⠘⣷⣄⠙⠛⠻⠿⠿⠿⠿⠿⠿⠿⠿⠿⠿⠿⠿⠿⠿⠿⠿⠟⠛⠋⣠⡿⠃⠀
 *               ⠀⠀⠈⠉⠛⠓⠒⠒⠒⠒⠒⠒⠒⠒⠒⠒⠒⠒⠒⠒⠒⠒⠒⠒⠛⠛⠉⠀⠀⠀
 *
 * TOUTE MODIFICATION DES FONCTIONS DOIT FAIRE L'OBJET DE VÉRIFICATION
 * SUR TOUS LES ÉCRANS QUALITÉ ET SUR LES ÉCRANS QUI CONTIENNENT DES
 * DONNÉES QUALITÉ, DE PLUS CERTAINES FONCTIONS SONT ÉGALEMENT REPRISES
 * CÔTÉ BACK DONC LES MODIFICATIONS DEVRONT ÉGALEMENT ÊTRE REPORTÉES
 * LÀ-BAS !!
 */

/**
 * @typedef {Object} Analysis
 * @property {string} remark - Code remarque
 * @property {number} result - Résultat de l'analyse
 * @property {string} parameter - Code du paramètre
 * @property {string} unit - Code de l'unité
 * @property {number} qualitometer - Id de la station sur laquelle l'analyse a été faite
 * @property {string} fraction - Code de la fraction
 */

/**
 * @typedef {Object} List
 * @property {string} remark - Code remarque
 * @property {number} enumerationA - Dénombrement A
 * @property {number} enumerationB - Dénombrement B
 * @property {number} enumerationC - Dénombrement C
 * @property {number} enumerationC2 - Dénombrement C'
 * @property {string} taxon - Code du taxon
 * @property {number} qualitometer - Id de la station
 */

/**
 * @typedef {Object} Threshold
 * @property {number} threshold1 - Premier seuil
 * @property {number} threshold2 - Second seuil
 * @property {number} threshold3 - Troisième seuil
 * @property {number} threshold4 - Quatrième seuil
 * @property {string} thresholdType - Type du seuil
 * @property {string} display - Mode d'affichage
 * @property {string} discriminant - Discriminant du seuil
 */

/**
 * @typedef {Object} ThresholdResult
 * @property {string} value - Label à afficher pour le résultat de l'analyse
 * @property {string} color - Couleur du seuil appliqué
 * @property {number} index - Indice du seuil
 * @property {Threshold} threshold - Seuil utilisé
 */

/**
 * @typedef {Object} IndexThreshold
 * @property {string} parameter - Code du paramètre
 * @property {string} unit - Code de l'unité
 * @property {number} station - Id de la station
 * @property {string} fraction - Code de la fraction
 */

const THRESHOLD = {
    PC: '0',
    TAXON: '1',
    INDICE: '2',
    ENVIRONMENTAL_PARAMETER: '3',
    STATE: '4',
}

const THRESHOLD_TYPE_CODE = {
    0: 'PC',
    1: 'TAXON',
    2: 'INDICE',
    3: 'ENVIRONMENTAL_PARAMETER',
    4: 'STATE',
}

const THRESHOLD_COLOR = {
    blue: PASTEL_COLOR.blue,
    green: PASTEL_COLOR.green,
    yellow: PASTEL_COLOR.yellow,
    orange: PASTEL_COLOR.orange,
    red: PASTEL_COLOR.red,
    white: PASTEL_COLOR.white,
}

const THRESHOLD_COLOR_BY_SIZE = {
    1: [THRESHOLD_COLOR.blue],
    2: [THRESHOLD_COLOR.blue, THRESHOLD_COLOR.red],
    3: [THRESHOLD_COLOR.blue, THRESHOLD_COLOR.orange, THRESHOLD_COLOR.red],
    4: [THRESHOLD_COLOR.blue, THRESHOLD_COLOR.yellow, THRESHOLD_COLOR.orange, THRESHOLD_COLOR.red],
    5: [THRESHOLD_COLOR.blue, THRESHOLD_COLOR.green, THRESHOLD_COLOR.yellow, THRESHOLD_COLOR.orange, THRESHOLD_COLOR.red],
    6: [THRESHOLD_COLOR.red, THRESHOLD_COLOR.orange, THRESHOLD_COLOR.yellow, THRESHOLD_COLOR.green, THRESHOLD_COLOR.blue, THRESHOLD_COLOR.white],
}

const THRESHOLD_TYPE = {
    INCREASING: 'INCREASING',
    DECREASING: 'DECREASING',
    BOUNDED: 'BOUNDED',
    INVERTED_BOUNDED: 'INVERTED_BOUNDED',
}

const THRESHOLD_DISPLAY = {
    INCREASING: 'INCREASING',
    DECREASING: 'DECREASING',
}

const THRESHOLD_DISCRIMINANT = {
    SUP_OR_EQUAL: 'SUP_OR_EQUAL',
    STRICTLY_SUP: 'STRICTLY_SUP',
    INF_OR_EQUAL: 'INF_OR_EQUAL',
    STRICTLY_INF: 'STRICTLY_INF',
    INF_OR_SUP_OR_EQUAL: 'INF_OR_SUP_OR_EQUAL',
    STRICTLY_INF_OR_SUP: 'STRICTLY_INF_OR_SUP',
}

const DISCRIMINANT_SIGN = {
    '>=': THRESHOLD_DISCRIMINANT.SUP_OR_EQUAL,
    '>': THRESHOLD_DISCRIMINANT.STRICTLY_SUP,
    '<=': THRESHOLD_DISCRIMINANT.INF_OR_EQUAL,
    '<': THRESHOLD_DISCRIMINANT.STRICTLY_INF,
    '<= =>': THRESHOLD_DISCRIMINANT.INF_OR_SUP_OR_EQUAL,
    '< >': THRESHOLD_DISCRIMINANT.STRICTLY_INF_OR_SUP,
}

const DEFAULT_DISCRIMINANT = {
    [THRESHOLD_TYPE.INCREASING]: THRESHOLD_DISCRIMINANT.SUP_OR_EQUAL,
    [THRESHOLD_TYPE.DECREASING]: THRESHOLD_DISCRIMINANT.INF_OR_EQUAL,
    [THRESHOLD_TYPE.BOUNDED]: THRESHOLD_DISCRIMINANT.INF_OR_SUP_OR_EQUAL,
}

const EXPORT_IMPORT_THRESHOLD_HEADERS = [
    { key: 'thresholdCode', isRequired: true },
    { key: 'thresholdName', isRequired: true },
    { key: 'type', isRequired: true },
    'codeParameterOrTaxon',
    'parameterOrTaxon',
    'codeUnit',
    'unit',
    'stationCode',
    'station',
    'fractionCode',
    'fraction',
    'thresholdType',
    'displayMode',
    'discriminant',
    { key: 'threshold1', isRequired: true },
    'threshold2',
    'threshold3',
    'threshold4',
    'hardnessCode',
    'hardness',
    'frTypeCode',
    'frType',
    'fishContextCode',
    'fishContext',
    'parent',
]

/**
 * Retourne le label à afficher pour un seuil borné en fonction de l'indice voulu
 * @param {number} index - L'indice calculé du seuil
 * @param {Threshold} threshold - Le seuil borné utilisé pour calculer l'indice
 * @returns {string} - Le label à afficher
 */
const getBoundedThresholdLabel = (index, threshold) => {
    if (isNil(threshold) || isNil(index)) return ''

    if (index === 0) return `<= ${threshold.threshold1}`
    if (index === 1) return `> ${threshold.threshold1} ${i18n.and} < ${threshold.threshold2}`
    if (index === 2) return `>= ${threshold.threshold2}`
    return ''
}

/**
 * Retourne le label à afficher pour un seuil croissant en fonction de l'indice voulu
 * @param {number} index - L'indice calculé du seuil
 * @param {Threshold} threshold - Le seuil croissant utilisé pour calculer l'indice
 * @returns {string} - Le label à afficher
 */
const getIncreasingThresholdLabel = (index, threshold) => {
    if (isNil(threshold) || isNil(index)) return ''

    const overSign = threshold.discriminant === THRESHOLD_DISCRIMINANT.SUP_OR_EQUAL ? '>=' : '>'
    const belowSign = threshold.discriminant === THRESHOLD_DISCRIMINANT.SUP_OR_EQUAL ? '<' : '<='

    if (index === 0) return `${belowSign} ${threshold.threshold1}`

    const maxIndice = maxBy([1, 2, 3, 4], nb => threshold[`threshold${nb}`] ?? Number.NEGATIVE_INFINITY)
    if (index === maxIndice) return `${overSign} ${threshold[`threshold${maxIndice}`]}`

    if (index > 0 && index < maxIndice) {
        if (threshold.display === THRESHOLD_DISPLAY.INCREASING) {
            return `${overSign} ${threshold[`threshold${index}`]} ${i18n.and} ${belowSign} ${threshold[`threshold${index + 1}`]}`
        }
        if (threshold.display === THRESHOLD_DISPLAY.DECREASING) {
            return `${belowSign} ${threshold[`threshold${index + 1}`]} ${i18n.and} ${overSign} ${threshold[`threshold${index}`]}`
        }
    }
    return ''
}

/**
 * Retourne le label à afficher pour un seuil décroissant en fonction de l'indice voulu
 * @param {number} index - L'indice calculé du seuil
 * @param {Threshold} threshold - Le seuil décroissant utilisé pour calculer l'indice
 * @returns {string} - Le label à afficher
 */
const getDecreasingThresholdLabel = (index, threshold) => {
    if (isNil(threshold) || isNil(index)) return ''

    const belowSign = threshold.discriminant === THRESHOLD_DISCRIMINANT.INF_OR_EQUAL ? '<=' : '<'
    const overSign = threshold.discriminant === THRESHOLD_DISCRIMINANT.INF_OR_EQUAL ? '>' : '>='

    if (index === 0) return `${overSign} ${threshold.threshold1}`

    const maxIndice = minBy([1, 2, 3, 4], nb => threshold[`threshold${nb}`] ?? Number.POSITIVE_INFINITY)
    if (index === maxIndice) return `${belowSign} ${threshold[`threshold${maxIndice}`]}`

    if (index > 0 && index < maxIndice) {
        if (threshold.display === THRESHOLD_DISPLAY.INCREASING) {
            return `${belowSign} ${threshold[`threshold${index}`]} ${i18n.and} ${overSign} ${threshold[`threshold${index + 1}`]}`
        }
        if (threshold.display === THRESHOLD_DISPLAY.DECREASING) {
            return `${overSign} ${threshold[`threshold${index + 1}`]} ${i18n.and} ${belowSign} ${threshold[`threshold${index}`]}`
        }
    }
    return ''
}

/**
 * Retourne le label à afficher pour un seuil en fonction de l'indice voulu
 * @param {number} index - L'indice calculé du seuil
 * @param {Threshold} threshold - Le seuil utilisé pour calculer l'indice
 * @returns {string} - Le label à afficher
 */
const getThresholdLabel = (index, threshold) => {
    if (isNil(threshold) || isNil(index)) return ''

    switch (threshold.thresholdType) {
        case THRESHOLD_TYPE.BOUNDED: return getBoundedThresholdLabel(index, threshold)
        case THRESHOLD_TYPE.INCREASING: return getIncreasingThresholdLabel(index, threshold)
        case THRESHOLD_TYPE.DECREASING: return getDecreasingThresholdLabel(index, threshold)
        default: return ''
    }
}

/**
 * Retourne le nom de la couleur pour utiliser le bon marker carto
 * @param {string} color - La couleur utilisé en hexa
 * @returns {string} - Le nom de la couleur
 */
const getMarkerColor = color => {
    return keys(THRESHOLD_COLOR).find(key => THRESHOLD_COLOR[key] === color)
}

/**
 * Retourne la liste des couleurs relative au seuil
 * @param {Threshold} threshold - Le seuil utilisé
 * @returns {string[]} - La liste des couleurs pour le seuil
 */
const getThresholdColors = (threshold) => {
    if (isNil(threshold)) return []
    if (threshold.thresholdType === THRESHOLD_TYPE.BOUNDED) return [THRESHOLD_COLOR.red, THRESHOLD_COLOR.blue, THRESHOLD_COLOR.red]

    // js cheat, adding boolean give a number: true = 1 and false = 0
    const nbThreshold = 1 + !isNil(threshold.threshold1) + !isNil(threshold.threshold2) + !isNil(threshold.threshold3) + !isNil(threshold.threshold4)
    return THRESHOLD_COLOR_BY_SIZE[nbThreshold]
}

/**
 * Compare un résultat d'analyse avec la valeur d'un seuil en fonction du discriminant du seuil
 * @param {*} result - Le résultat d'analyse
 * @param {*} th - La valeur du seuil
 * @param {*} discriminant - Le discriminant du seuil
 * @returns {boolean} - Résultat de la comparaison
 */
const compareToThreshold = (result, th, discriminant) => {
    if (isNil(result) || isNil(th) || isNil(discriminant)) return
    switch (discriminant) {
        case THRESHOLD_DISCRIMINANT.SUP_OR_EQUAL: return result >= th
        case THRESHOLD_DISCRIMINANT.STRICTLY_SUP: return result > th
        case THRESHOLD_DISCRIMINANT.INF_OR_EQUAL: return result <= th
        case THRESHOLD_DISCRIMINANT.STRICTLY_INF: return result < th
        default: return false
    }
}

/**
 * Calcule l'indice du seuil pour une analyse
 * @param {Threshold} threshold - The threshold to use
 * @param {number} result - Analysis's result
 * @returns {ThresholdResult} - The calculated result
 */
const getThresholdResult = (threshold, result) => {
    if (isNil(threshold) || isNil(result)) return {}

    if (threshold.thresholdType === THRESHOLD_TYPE.BOUNDED) {
        if (compareToThreshold(result, threshold.threshold1, THRESHOLD_DISCRIMINANT.INF_OR_EQUAL)) return { index: 0, color: THRESHOLD_COLOR.red }
        if (compareToThreshold(result, threshold.threshold2, THRESHOLD_DISCRIMINANT.SUP_OR_EQUAL)) return { index: 2, color: THRESHOLD_COLOR.red }
        return { index: 1, color: THRESHOLD_COLOR.blue }
    }

    const listThresholds = [
        threshold.thresholdType === THRESHOLD_TYPE.INCREASING ? Number.NEGATIVE_INFINITY : Number.POSITIVE_INFINITY,
        threshold.threshold1,
        threshold.threshold2,
        threshold.threshold3,
        threshold.threshold4,
    ]
    const nbThreshold = listThresholds.filter(th => !isNil(th)).length
    const index = listThresholds.findLastIndex(th => compareToThreshold(result, th, threshold.discriminant))
    return {
        index,
        color: THRESHOLD_COLOR_BY_SIZE[nbThreshold][index],
    }
}

const calculateHighestIndexThreshold = (threshold) => {
    return findLast([1, 2, 3, 4], nb => !isNil(threshold[`threshold${nb}`]))
}

/**
 * Calcule l'indice pour un résultat d'analyse minimal
 * @param {Threshold} threshold - Le seuil utilisé
 * @returns {number} - L'indice calculé
 */
const calculateMinValueIndexThreshold = (threshold) => {
    if (isNil(threshold)) return
    if (threshold.thresholdType === THRESHOLD_TYPE.BOUNDED) return 0
    if (threshold.thresholdType === THRESHOLD_TYPE.INCREASING) return 0
    return minBy([1, 2, 3, 4], nb => threshold[`threshold${nb}`] ?? Number.POSITIVE_INFINITY)
}

/**
 * Calcule l'indice pour un résultat d'analyse maximal
 * @param {Threshold} threshold - Le seuil utilisé
 * @returns {number} - L'indice calculé
 */
const calculateMaxValueIndexThreshold = (threshold) => {
    if (isNil(threshold)) return
    if (threshold.thresholdType === THRESHOLD_TYPE.BOUNDED) return 2
    if (threshold.thresholdType === THRESHOLD_TYPE.DECREASING) return 0
    return maxBy([1, 2, 3, 4], nb => threshold[`threshold${nb}`] ?? Number.NEGATIVE_INFINITY)
}

/**
 * Calcule l'indice du seuil pour une analyse
 * @param {Analysis} analysis - L'analyse à comparer
 * @param {Threshold} threshold - Le seuil à utiliser
 * @returns {number} - L'indice calculé
 */
const calculateIndexThreshold = (analysis, threshold) => {
    if (isNil(threshold) || isNil(analysis?.result)) return

    switch (analysis.remark) {
        case '0': // Analyse non faite
            return
        case '2': // < seuil de détection
        case '7': // Traces
        case '9': // Dénombrement < résultat
        case '10': // < au seuil de quantification
            return calculateMinValueIndexThreshold(threshold)
        case '8': // Dénombrement > résultat
        case '3': // > seuil de saturation
            return calculateMaxValueIndexThreshold(threshold)
        default:
            return getThresholdResult(threshold, analysis.result).index
    }
}

/**
 * Retourne si un analyse dépasse le dernier seuil ou non
 * @param {Analysis} analysis - L'objet d'analyse
 * @param {Threshold} threshold - Le seuil à utiliser
 * @returns {boolean} - Le resultat du test
 */
const isNotConform = (analysis, threshold) => {
    if (isNil(analysis) || isNil(threshold)) return false

    const index = calculateIndexThreshold(analysis, threshold)
    const maxIndex = calculateHighestIndexThreshold(threshold)
    if (threshold.thresholdType === THRESHOLD_TYPE.BOUNDED) return index !== 1
    return index === maxIndex
}

/**
 * Retourne si une analyse ne dépasse aucun seuil ou non
 * @param {Analysis} analysis - L'objet d'analyse
 * @param {Threshold} threshold - Le seuil à utiliser
 * @returns {boolean} - Le resultat du test
 */
const isValid = (analysis, threshold) => {
    if (isNil(analysis)) return false
    if (isNil(threshold)) return true

    const index = calculateIndexThreshold(analysis, threshold)
    if (threshold.thresholdType === THRESHOLD_TYPE.BOUNDED) return index === 1
    return index === 0
}

/**
 * Calcule le résultat de l'application d'un seuil sur une analyse
 * @param {Analysis} analysis - L'analyse à comparer
 * @param {Threshold} threshold - Le seuil qualité à utiliser
 * @returns {ThresholdResult} - Le resultat
 */
const calculateThresholdResult = (analysis, threshold) => {
    if (isNil(threshold) || isNil(analysis)) return { value: calculateValue(analysis), color: THRESHOLD_COLOR.white }

    switch (analysis.remark) {
        case '0': { // Analyse non faite
            return {
                value: '',
                color: THRESHOLD_COLOR.white,
                index: undefined,
                threshold,
            }
        }
        case '2': // < seuil de détection
        case '7': // Traces
        case '9': // Dénombrement < résultat
        case '10': { // < au seuil de quantification
            return {
                value: !isNil(analysis.result) ? `<${round(analysis.result, 5)}` : '',
                color: threshold.thresholdType === THRESHOLD_TYPE.INCREASING ? THRESHOLD_COLOR.blue : THRESHOLD_COLOR.red,
                index: calculateMinValueIndexThreshold(threshold),
                threshold,
            }
        }
        case '8': // Dénombrement > résultat
        case '3': // > seuil de saturation
            return {
                value: !isNil(analysis.result) ? `>${round(analysis.result, 5)}` : '',
                color: threshold.thresholdType === THRESHOLD_TYPE.DECREASING ? THRESHOLD_COLOR.blue : THRESHOLD_COLOR.red,
                index: calculateMaxValueIndexThreshold(threshold),
                threshold,
            }
        default:
            const { index, color } = getThresholdResult(threshold, analysis.result)
            return {
                value: !isNil(analysis.result) ? `${round(analysis.result, 5)}` : '',
                color,
                index,
                threshold,
            }
    }
}

/**
 * Fonction qui crée un index de seuil qualité
 * @param {IndexThreshold} indexThreshold - Object contenant le code du paramètre, le code de l'unité, l'id de la station et le code de la fraction
 * @returns {string} - Le nouveau index
 */
const createThresholdKey = ({ parameter = '#', unit = '#', station = '#', fraction = '#' }) => `${parameter}:${unit}:${station}:${fraction}`

/*
Tout n'est pas géré par principe d'opti, si besoin les ajouter et faire de même coté back
il y a un ordre de priorité sur l'application des seuils, d'ou les 16 .find
Enjoy !!

1) paramètre unité station fraction -> yes
2) paramètre unité station -> yes
3) paramètre unité fraction -> yes
4) paramètre station fraction
5) unité station fraction
6) paramètre unité -> yes
7) paramètre station -> yes
8) unité station
9) paramètre fraction -> yes
10) unité fraction
11) station fraction
12) paramètre -> yes
13) unité -> yes
14) station -> yes
15) fraction
16) -> yes

Nommage:
P = paramètre, U = unité, S = station, F = fraction
Ex: thresholdPUS -> parameterCode, unit, station sont définie et fraction est undefined
*/
/**
 * Fonction qui recherche le seuil à appliquer à l'analyse
 * @param {Threshold[]} thresholds - Liste des seuils qualités
 * @param {Analysis} Analysis - Analyse sur laquelle appliquer un seuil
 * @returns {Threshold} - Le seuil à appliquer
 */
const findThreshold = (thresholds = [], { parameter, unit, station, fraction }) => {
    const thresholdPUSF = thresholds.find(t => t.parameterCode === parameter && t.unit === unit && t.station === station && t.fraction === fraction)
    if (!isNil(thresholdPUSF)) return thresholdPUSF

    const thresholdPUS = thresholds.find(t => t.parameterCode === parameter && t.unit === unit && t.station === station && isNil(t.fraction))
    if (!isNil(thresholdPUS)) return thresholdPUS

    const thresholdPUF = thresholds.find(t => t.parameterCode === parameter && t.unit === unit && isNil(t.station) && t.fraction === fraction)
    if (!isNil(thresholdPUF)) return thresholdPUF

    // const thresholdPSF = thresholds.find(t => t.parameterCode === parameter && isNil(t.unit) && t.station === station && t.fraction === fraction)
    // if (!isNil(thresholdPSF)) return thresholdPSF

    // const thresholdUSF = thresholds.find(t => isNil(t.parameterCode) && t.unit === unit && t.station === station && t.fraction === fraction)
    // if (!isNil(thresholdUSF)) return thresholdUSF

    const thresholdPU = thresholds.find(t => t.parameterCode === parameter && t.unit === unit && isNil(t.station) && isNil(t.fraction))
    if (!isNil(thresholdPU)) return thresholdPU

    const thresholdPS = thresholds.find(t => t.parameterCode === parameter && isNil(t.unit) && t.station === station && isNil(t.fraction))
    if (!isNil(thresholdPS)) return thresholdPS

    // const thresholdUS = thresholds.find(t => isNil(t.parameterCode) && t.unit === unit && t.station === station && isNil(t.fraction))
    // if (!isNil(thresholdUS)) return thresholdUS

    const thresholdPF = thresholds.find(t => t.parameterCode === parameter && isNil(t.unit) && isNil(t.station) && t.fraction === fraction)
    if (!isNil(thresholdPF)) return thresholdPF

    // const thresholdUF = thresholds.find(t => isNil(t.parameterCode) && t.unit === unit && isNil(t.station) && t.fraction === fraction)
    // if (!isNil(thresholdUF)) return thresholdUF

    // const thresholdSF = thresholds.find(t => isNil(t.parameterCode) && isNil(t.unit) && t.station === station && t.fraction === fraction)
    // if (!isNil(thresholdSF)) return thresholdSF

    const thresholdP = thresholds.find(t => t.parameterCode === parameter && isNil(t.unit) && isNil(t.station) && isNil(t.fraction))
    if (!isNil(thresholdP)) return thresholdP

    const thresholdU = thresholds.find(t => isNil(t.parameterCode) && t.unit === unit && isNil(t.station) && isNil(t.fraction))
    if (!isNil(thresholdU)) return thresholdU

    const thresholdS = thresholds.find(t => isNil(t.parameterCode) && isNil(t.unit) && t.station === station && isNil(t.fraction))
    if (!isNil(thresholdS)) return thresholdS

    // const thresholdF = thresholds.find(t => isNil(t.parameterCode) && isNil(t.unit) && isNil(t.station) && t.fraction === fraction)
    // if (!isNil(thresholdF)) return thresholdF

    // default threshold
    return thresholds.find(t => isNil(t.parameterCode) && isNil(t.unit) && isNil(t.station) && isNil(t.fraction))
}

/**
 * Fonction qui recherche le seuil à appliquer à l'analyse
 * @param {Object.<string, Threshold>} [indexedThresholds={}] - Un object qui correspond à la liste indexé des seuils qualité à utiliser
 * @param {Analysis} Analysis - Analyse sur laquelle appliquer un seuil
 * @returns {Threshold} - Le seuil à appliquer
 */
const findIndexedThreshold = (indexedThresholds = {}, { parameter, unit, station, fraction }) => {
    return indexedThresholds[createThresholdKey({ parameter, unit, station, fraction })]
        ?? indexedThresholds[createThresholdKey({ parameter, unit, station })]
        ?? indexedThresholds[createThresholdKey({ parameter, unit, fraction })]
        // ?? indexedThresholds[createThresholdKey({ parameter, station, fraction })]
        // ?? indexedThresholds[createThresholdKey({ unit, station, fraction })]
        ?? indexedThresholds[createThresholdKey({ parameter, unit })]
        ?? indexedThresholds[createThresholdKey({ parameter, unit, station, fraction })]
        ?? indexedThresholds[createThresholdKey({ parameter, station })]
        // ?? indexedThresholds[createThresholdKey({ unit, station })]
        ?? indexedThresholds[createThresholdKey({ parameter, fraction })]
        // ?? indexedThresholds[createThresholdKey({ unit, fraction })]
        // ?? indexedThresholds[createThresholdKey({ station, fraction })]
        ?? indexedThresholds[createThresholdKey({ parameter })]
        ?? indexedThresholds[createThresholdKey({ unit })]
        ?? indexedThresholds[createThresholdKey({ station })]
        // ?? indexedThresholds[createThresholdKey({ fraction })]
        ?? indexedThresholds[createThresholdKey({})]
}

/**
 * Calcule le résultat de l'application des seuils sur une analyse
 * @param {Analysis} [analysis] - L'analyse à comparer
 * @param {Threshold[]} [thresholds=[]] - La liste des seuils qualité à utiliser
 * @returns {ThresholdResult} - Le resultat
 */
const calculateThresholdsResult = (analysis, thresholds = []) => {
    const threshold = findThreshold(thresholds, analysis)
    return calculateThresholdResult(analysis, threshold)
}

/**
 * Calcule le résultat de l'application des seuils sur une analyse
 * @param {Analysis} [analysis] - L'analyse à comparer
 * @param {Object.<string, Threshold>} [indexedThresholds={}] - Un object qui correspond à la liste indexé des seuils qualité à utiliser
 * @returns {ThresholdResult} - Le resultat
 */
const calculateIndexedThresholdsResult = (analysis, indexedThresholds = {}) => {
    const threshold = findIndexedThreshold(indexedThresholds, analysis)
    return calculateThresholdResult(analysis, threshold)
}


/**
 * Calcule le résultat de l'application d'un seuil sur un dénombrement
 * @param {List} list - Le dénombrement à comparer
 * @param {Threshold} threshold - Le seuil qualité à utiliser
 * @returns {ThresholdResult} - Le resultat
 */
const calculateTaxonThresholdResult = (list, threshold) => {
    const result = (list.enumerationA ?? 0) + (list.enumerationB ?? 0) + (list.enumerationC ?? 0) + (list.enumerationC2 ?? 0)
    switch (list.remark) {
        case '0': // Analyse non faite
            return {
                value: '',
                result,
                color: THRESHOLD_COLOR.white,
                threshold,
                index: 0,
            }
        case '2': // < seuil de détection
        case '7': // Traces
        case '10': // < au seuil de quantification
            return {
                value: !isNil(result) ? `<${round(result, 5)}` : '',
                result,
                color: threshold.thresholdType === THRESHOLD_TYPE.INCREASING ? THRESHOLD_COLOR.blue : THRESHOLD_COLOR.red,
                threshold,
                index: calculateMinValueIndexThreshold(threshold),
            }
        case '3': // > seuil de saturation
            return {
                value: !isNil(result) ? `>${round(result, 5)}` : '',
                result,
                color: threshold.thresholdType === THRESHOLD_TYPE.DECREASING ? THRESHOLD_COLOR.blue : THRESHOLD_COLOR.red,
                threshold,
                index: calculateMaxValueIndexThreshold(threshold),
            }
        default:
            const { index, color } = getThresholdResult(threshold, result)
            return {
                value: !isNil(result) ? `${round(result, 5)}` : '',
                result,
                color,
                threshold,
                index,
            }
    }
}

/**
 * Calcule le résultat de l'application des seuils sur une analyse
 * @param {List} list - Le dénombrement à comparer
 * @param {Threshold[]} [thresholds=[]] - La liste des seuils qualité à utiliser
 * @returns {ThresholdResult} - Le resultat
 */
const calculateTaxonThresholdsResult = (list, thresholds = []) => {
    const threshold = findThreshold(thresholds, { parameter: list.taxon, qualitometer: list.qualitometer })
    return calculateTaxonThresholdResult(list, threshold)
}

export {
    THRESHOLD,
    THRESHOLD_TYPE_CODE,
    THRESHOLD_COLOR,
    THRESHOLD_COLOR_BY_SIZE,
    THRESHOLD_TYPE,
    THRESHOLD_DISPLAY,
    THRESHOLD_DISCRIMINANT,
    DISCRIMINANT_SIGN,
    DEFAULT_DISCRIMINANT,
    EXPORT_IMPORT_THRESHOLD_HEADERS,

    getBoundedThresholdLabel,
    getIncreasingThresholdLabel,
    getDecreasingThresholdLabel,
    getThresholdLabel,
    getMarkerColor,
    getThresholdColors,

    compareToThreshold,
    createThresholdKey,
    findThreshold,
    findIndexedThreshold,

    calculateIndexThreshold,
    isNotConform,
    isValid,
    calculateThresholdResult,
    calculateThresholdsResult,
    calculateIndexedThresholdsResult,

    calculateTaxonThresholdResult,
    calculateTaxonThresholdsResult,
}